import isFlagOn from '../isFlagOn'
import notDeleted from '../notDeleted'

// AI-CONTEXT: See docs/features/automatische-deelname-onderzoek.md

/**
 * Welke vlag op het onderzoek geldt voor welke soort entiteit.
 *
 * `Research.AppliesTo` — bewust niet `Participation`, want die naam bestaat al
 * op `Personas` (participatieladder) en in `HouseholdParticipations`
 * (programma-deelname) en betekent daar iets anders.
 */
const FLAG_BY_KIND = {
    adress: 'allHouseholds',
    persona: 'allPersonas',
}

/**
 * Geldt dit onderzoek voor álle huishoudens (of alle medewerkers)?
 *
 * De schakelaar in de onderzoeksbouwer schrijft de tekst 'true'/'false'; een
 * script schrijft een echte boolean. Beide vormen tellen, alles wat we niet
 * herkennen is uit — via de gedeelde `isFlagOn`, nooit met `=== 'true'`.
 * AI-CONTEXT: See docs/pitfalls.md#ui-togglebutton-string-only-branches
 *
 * @param {object|null|undefined} research - Een `Research`-document
 * @param {'adress'|'persona'} kind - Voor welke soort entiteit je het vraagt
 * @returns {boolean}
 */
const isResearchForAll = (research, kind) => {
    if (!research || !notDeleted(research)) return false
    const flag = FLAG_BY_KIND[kind]
    if (!flag) return false
    return isFlagOn(research.AppliesTo?.[flag])
}

/**
 * De onderzoeken waaraan één huishouden of medewerker meedoet.
 *
 * Dit is DE regel, en hij staat maar op één plek: wat met de hand gekoppeld is
 * (`Adresses.Research[]` / `Personas.Research[]`) plus elk onderzoek dat via
 * de schakelaar voor deze hele soort geldt. Tot 2026-09-15 was het lijstje de
 * enige aan-knop, en die werd bij 38% van de mensen die op naam meedoen nooit
 * omgezet — die mensen bestonden voor de signalering niet.
 *
 * Elke afnemer (signalering, Onderzoeken-tabs, Overzicht-kaarten, deel-links,
 * agenda-teller, effectgrafieken) gaat door deze functie. Twee kopieën van
 * dezelfde regel lopen uiteen.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * Zolang de onderzoeken nog niet geladen zijn (`researches` geen array), komt
 * alleen de handmatige set terug — de aanroeper moet dan zelf nog niet beweren
 * dat er "niets gekoppeld" is.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {object} [args]
 * @param {'adress'|'persona'} [args.kind] - Soort entiteit
 * @param {Array<string>} [args.linkedIds] - De handmatig gekoppelde `Research.ID`'s
 * @param {Array<object>|null} [args.researches] - Alle `Research`-documenten van de
 *   organisatie, in de gewenste volgorde (meestal op `Position`)
 * @returns {Array<string>} Handmatige ids eerst (in hun eigen volgorde), dan de
 *   automatische in lijstvolgorde; zonder dubbelen
 */
const effectiveResearchIds = ({ kind, linkedIds, researches } = {}) => {
    const linked = (Array.isArray(linkedIds) ? linkedIds : []).filter((id) => typeof id === 'string' && id !== '')
    const automatic = Array.isArray(researches)
        ? researches.filter((research) => isResearchForAll(research, kind)).map((research) => research.ID)
        : []
    return [...new Set([...linked, ...automatic])]
}

/**
 * Zelfde regel, maar dan de documenten: voor afnemers die de titel of de
 * vragenlijst nodig hebben. Een handmatig gekoppeld id zonder (levend)
 * document valt af.
 *
 * @param {object} [args]
 * @param {'adress'|'persona'} [args.kind]
 * @param {Array<string>} [args.linkedIds]
 * @param {Array<object>|null} [args.researches]
 * @returns {Array<object>}
 */
const effectiveResearchDocs = ({ kind, linkedIds, researches } = {}) => {
    const docs = (Array.isArray(researches) ? researches : []).filter(notDeleted)
    return effectiveResearchIds({ kind, linkedIds, researches })
        .map((id) => docs.find((research) => research.ID === id))
        .filter(Boolean)
}

export default effectiveResearchIds
export { effectiveResearchDocs, isResearchForAll }
